import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./ConsultationsPage.scss";

import ConsultationCard from "./ConsultationCard";
import ConsultationRatings from "./ConsultationRatings";
import ConsultationsSkeleton, {
  ConsultationsPageSkeleton,
} from "./ConsultationsSkeleton";
import CreateConsultationModal from "./CreateConsultationModal";
import Button from "../../../atoms/Button/Button";
import PageHeader from "../../../molecules/PatientDetailsV2/PageHeader/PageHeader";
import { RootState } from "../../../store/store";
import {
  getPastConsultationsRequest,
  getUpcomingConsultationsRequest,
} from "../../../store/slices/ConsultationSlice";
import {
  ConsultationList,
  ConsultationStat,
} from "../../../types/consultation";

import { ReactComponent as PlusIcon } from "../../../assets/icons/patient-layout-v2/consults/PlusIcon.svg";

// The server sends "-" for a stat it cannot fill.
const EMPTY_STAT = "-";

const Stat: React.FC<{ stat: ConsultationStat }> = ({ stat }) => {
  const isEmpty = !stat.value || stat.value === EMPTY_STAT;

  return (
    <div className="cs-stat">
      <div className="k">{stat.label}</div>
      <div className={`v ${isEmpty ? "empty" : ""}`}>
        {isEmpty ? "—" : stat.value}
        {!isEmpty && stat.caption && <small>{stat.caption}</small>}
      </div>
    </div>
  );
};

const ConsultationsPage: React.FC = () => {
  const dispatch = useDispatch();
  const [category, setCategory] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { patientDetails } = useSelector((state: RootState) => state.patient);
  const patientId = patientDetails?.data?.patient_id;

  const { upcomingConsultations, pastConsultations } = useSelector(
    (state: RootState) => state.consultation,
  );

  const loadConsultations = useCallback(() => {
    if (!patientId) return;

    // Two calls: the server splits upcoming from past, and only the past list
    // is paged. Booking calls this too, so a new appointment shows up without
    // a reload.
    dispatch(
      getUpcomingConsultationsRequest({ patient_id: patientId, category }),
    );
    dispatch(
      getPastConsultationsRequest({
        patient_id: patientId,
        category,
        page_no: 1,
      }),
    );
  }, [dispatch, patientId, category]);

  useEffect(() => {
    loadConsultations();
  }, [loadConsultations]);

  const upcomingData = upcomingConsultations.data;
  const pastData = pastConsultations.data;

  // Both responses carry the shared blocks. The upcoming call is the one that
  // asks for the summary, so prefer it and fall back while it is in flight.
  const shell = upcomingData || pastData;
  const stats = shell?.stats || [];
  const categories = shell?.categories || [];
  const ratingSummary = shell?.ratingSummary;
  const newConsultationCta = shell?.newConsultationCta;

  const upcomingList = upcomingData?.list;
  const pastList = pastData?.list;
  const pagination = pastList?.pagination;

  const isFirstLoad =
    !shell && (upcomingConsultations.isLoading || pastConsultations.isLoading);
  const isEmpty =
    !!shell && !upcomingList?.rows?.length && !pastList?.rows?.length;

  const pastRows = pastList?.rows;
  // Page one is covered by the full-screen skeleton; anything after it is an
  // append under rows that are already on screen.
  const isLoadingMore = pastConsultations.isLoading && !!pastRows?.length;

  // A failed page stops the paging: the coach can retry by tapping again, but
  // it should not be presented as if more rows are waiting.
  const canLoadMore = !!pagination?.hasMore && !pastConsultations.errors;
  const hasReachedEnd = !!pastRows?.length && !canLoadMore;

  const loadMore = () => {
    if (!patientId || !canLoadMore || pastConsultations.isLoading) return;

    dispatch(
      getPastConsultationsRequest({
        patient_id: patientId,
        category,
        page_no: (pagination?.pageNo ?? 1) + 1,
      }),
    );
  };

  const renderGroup = (list?: ConsultationList, isUpcoming?: boolean) => {
    if (!list?.rows?.length) return null;

    return (
      <>
        <div className="cs-group-label">
          {list.title}
          <span className="ln" />
          {list.total}
        </div>
        <div className="cs-list">
          {list.rows.map((row) => (
            <ConsultationCard
              key={row.appointmentId}
              row={row}
              isUpcoming={isUpcoming}
            />
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="consultations-page">
      <PageHeader title={shell?.title || "Consultations"} />

      {isFirstLoad ? (
        <ConsultationsSkeleton />
      ) : (
        !!stats.length && (
          <div className="cs-stats">
            {stats.map((stat) => (
              <Stat key={stat.key} stat={stat} />
            ))}
          </div>
        )
      )}

      {ratingSummary && ratingSummary.average != null && (
        <ConsultationRatings summary={ratingSummary} />
      )}

      {!isFirstLoad && (
        <div className="cs-toolbar">
          <div className="cs-filters">
            {categories.map((option) => (
              <Button
                key={option.key}
                className="btn-chip"
                size="sm"
                variant={category === option.key ? "contained" : "outlined"}
                onClick={() => setCategory(option.key)}
              >
                {option.label}
              </Button>
            ))}
          </div>
          {newConsultationCta?.enabled !== false && (
            <Button
              size="sm"
              title={newConsultationCta?.disabledReason || undefined}
              iconStart={<PlusIcon />}
              onClick={() => setShowCreateModal(true)}
            >
              {newConsultationCta?.label || "New consultation"}
            </Button>
          )}
        </div>
      )}

      {isEmpty && (
        <div className="cs-empty">
          No consultations{category !== "all" ? " for this category" : ""} yet.
        </div>
      )}

      {renderGroup(upcomingList, true)}
      {renderGroup(pastList)}

      {isLoadingMore && <ConsultationsPageSkeleton />}

      {canLoadMore && !isLoadingMore && (
        <div className="cs-more">
          <Button
            variant="outlined"
            size="sm"
            rounded="full"
            onClick={loadMore}
          >
            Load more
          </Button>
        </div>
      )}

      {hasReachedEnd && !isLoadingMore && (
        <div className="cs-end">You have reached the end</div>
      )}

      {showCreateModal && (
        <CreateConsultationModal
          onClose={() => setShowCreateModal(false)}
          onBooked={loadConsultations}
        />
      )}
    </div>
  );
};

export default ConsultationsPage;
